import { gsap, ScrollTrigger, motion, withLoader, endIntro, reveals, fitStages, refreshAfterFonts, prepareStroke } from "./common.js";

const root = document.querySelector(".qb-home");
const q = (s, r = root) => r.querySelector(s);
const qa = (s, r = root) => gsap.utils.toArray(r.querySelectorAll(s));

fitStages(root);
setupTabs();
setupSpotlight();
const film = setupFilm();

if (motion) {
  setupMagnets();
  setupTicketTilt();
  scenes();
  if (withLoader) intro();
  else heroIn();
  refreshAfterFonts();
}

// ---------------------------------------------------------------------------
// Loading screen and hero entrance

function heroBits() {
  return {
    words: qa(".qb-hero .qb-word"),
    bits: qa(".qb-hero .qb-lead, .qb-hero .qb-ctas, .qb-hero .qb-note"),
  };
}

function intro() {
  const ring = q(".qb-loader .qb-ring");
  const drip = q(".qb-loader .qb-drip");
  prepareStroke(ring);
  gsap.set(".qb-loader__mark", { opacity: 1 });
  gsap.set(drip, { scale: 0, svgOrigin: "42 43" }); // the drop grows from the gap in the ring
  const { words, bits } = heroBits();
  gsap.set(words, { yPercent: 118, rotate: 5 });
  gsap.set(bits, { autoAlpha: 0, y: 26 });
  gsap.set(".qb-hero .qb-chip", { autoAlpha: 0, y: 18 });
  gsap.set(".qb-stagewrap", { autoAlpha: 0, y: 160 });
  gsap.set(".qb-nav", { yPercent: -100 });
  gsap
    .timeline({ onComplete: endIntro })
    .to(ring, { strokeDashoffset: 0, duration: 1.1, ease: "power2.inOut" })
    .to(drip, { scale: 1, duration: 0.8, ease: "back.out(2.4)" }, 0.95)
    .fromTo(".qb-loader__word", { autoAlpha: 0, y: 12 }, { autoAlpha: 1, y: 0, duration: 0.6, ease: "power3.out" }, 0.5)
    .fromTo(".qb-loader__bar i", { scaleX: 0 }, { scaleX: 1, duration: 1.6, ease: "power2.inOut" }, 0.1)
    .to(".qb-loader__inner", { y: -34, autoAlpha: 0, duration: 0.5, ease: "power2.in" }, 1.85)
    .to(".qb-loader", { clipPath: "inset(0% 0% 100% 0%)", duration: 1.05, ease: "expo.inOut" }, 2.0)
    .to(".qb-hero .qb-chip", { autoAlpha: 1, y: 0, duration: 0.9, ease: "expo.out" }, 2.4)
    .to(words, { yPercent: 0, rotate: 0, duration: 1.35, ease: "expo.out", stagger: 0.07 }, 2.45)
    .to(bits, { autoAlpha: 1, y: 0, duration: 1.1, ease: "expo.out", stagger: 0.09 }, 2.85)
    .to(".qb-nav", { yPercent: 0, duration: 0.9, ease: "expo.out" }, 2.6)
    .to(".qb-stagewrap", { autoAlpha: 1, y: 0, duration: 1.7, ease: "expo.out" }, 2.95);
}

// Repeat visits in a session skip the loading screen but keep the entrance.
function heroIn() {
  const { words, bits } = heroBits();
  gsap
    .timeline({ defaults: { ease: "expo.out" } })
    .from(".qb-hero .qb-chip", { autoAlpha: 0, y: 18, duration: 0.8 })
    .from(words, { yPercent: 118, rotate: 5, duration: 1.2, stagger: 0.06 }, 0.05)
    .from(bits, { autoAlpha: 0, y: 26, duration: 1, stagger: 0.08 }, 0.35)
    .from(".qb-stagewrap", { autoAlpha: 0, y: 140, duration: 1.5 }, 0.45);
}

// ---------------------------------------------------------------------------
// Scroll scenes

function scenes() {
  const nav = q(".qb-nav");
  let shown = true;
  ScrollTrigger.create({
    start: 0,
    end: "max",
    onUpdate: (self) => {
      const want = self.scroll() < 240 || self.direction < 0;
      if (want !== shown) {
        shown = want;
        gsap.to(nav, { yPercent: want ? 0 : -100, duration: 0.5, ease: "power3.out", overwrite: "auto" });
      }
    },
  });

  gsap.fromTo(".qb-window", { rotateX: 26, scale: 0.88 }, {
    rotateX: 0, scale: 1, ease: "none",
    scrollTrigger: { trigger: ".qb-stagewrap", start: "top 92%", end: "top 14%", scrub: 1 },
  });
  gsap.fromTo(".qb-glow", { opacity: 0.35, scale: 0.8 }, {
    opacity: 1, scale: 1.15, ease: "none",
    scrollTrigger: { trigger: ".qb-stagewrap", start: "top bottom", end: "bottom top", scrub: true },
  });
  gsap.to(".qb-hero__copy", {
    yPercent: -24, autoAlpha: 0.1, ease: "none",
    scrollTrigger: { trigger: ".qb-hero", start: "top top", end: "42% top", scrub: true },
  });

  story();
  reveals(root);

  // Platform cards load in as they arrive.
  const cards = qa(".qb-card.qb-lazy");
  gsap.set(qa(".qb-card .qb-pop"), { autoAlpha: 0, y: 16 });
  gsap.set(cards, { y: 70, rotateX: 9, transformOrigin: "50% 0%" });
  ScrollTrigger.batch(cards, {
    start: "top 90%",
    once: true,
    onEnter: (batch) => {
      gsap.to(batch, { y: 0, rotateX: 0, duration: 1.3, ease: "expo.out", stagger: 0.1 });
      batch.forEach((el, i) => gsap.delayedCall(0.45 + i * 0.14, () => loadCard(el)));
    },
  });

  ScrollTrigger.create({
    trigger: ".qb-screen",
    start: "top 72%",
    end: "bottom 12%",
    onEnter: film.enter,
    onEnterBack: film.enter,
    onLeave: film.pauseForScroll,
    onLeaveBack: film.pauseForScroll,
  });

  gsap.fromTo(".qb-plain__panel", { scale: 0.93, borderRadius: 96 }, {
    scale: 1, borderRadius: 48, ease: "none",
    scrollTrigger: { trigger: ".qb-plain", start: "top bottom", end: "top 15%", scrub: true },
  });
  gsap.to(qa(".qb-says .w"), {
    color: (i, el) => (el.classList.contains("k") ? "#0b7f93" : "#0b0e13"),
    stagger: 0.1, ease: "none",
    scrollTrigger: { trigger: ".qb-says", start: "top 78%", end: "bottom 48%", scrub: 0.6 },
  });

  const track = q(".qb-marquee__track");
  const loop = gsap.to(track, { xPercent: -50, duration: 30, ease: "none", repeat: -1 });
  ScrollTrigger.create({
    trigger: ".qb-marquee",
    start: "top bottom",
    end: "bottom top",
    onUpdate: (self) => {
      const v = self.getVelocity();
      gsap.to(track, { skewX: gsap.utils.clamp(-10, 10, v / -260), duration: 0.45, ease: "power3.out", overwrite: "auto" });
      loop.timeScale(1 + Math.min(5, Math.abs(v) / 500));
      gsap.to(loop, { timeScale: 1, duration: 1.2, delay: 0.1, ease: "power2.out", overwrite: true });
    },
  });

  gsap.from(qa(".qb-fig__n b, .qb-fig__n small"), {
    yPercent: 110, duration: 1.3, ease: "expo.out", stagger: 0.08,
    scrollTrigger: { trigger: ".qb-own__grid", start: "top 82%" },
  });

  gsap.fromTo(".qb-ticketwrap", { rotateX: 62, y: 140, autoAlpha: 0, transformPerspective: 1400 }, {
    rotateX: 0, y: 0, autoAlpha: 1, ease: "none",
    scrollTrigger: { trigger: ".qb-invite__stage", start: "top 95%", end: "top 35%", scrub: 1 },
  });

  gsap.fromTo(qa(".qb-mark span"), { yPercent: 105 }, {
    yPercent: 0, ease: "none", stagger: 0.05,
    scrollTrigger: { trigger: ".qb-foot", start: "top bottom", end: "bottom bottom", scrub: 0.8 },
  });
}

// A dozen hot services cool and fuse into one program as you scroll.
function story() {
  const forge = q(".qb-forge");
  const tiles = qa(".qb-tile");
  const beats = qa(".qb-beat");
  const steam = qa(".qb-steam i");
  const sats = qa(".qb-sat");
  const specs = qa(".qb-spec");
  const ingot = q(".qb-ingot");
  const burst = q(".qb-burst");

  gsap.set(forge, { "--h": 1 });
  gsap.set(beats, { autoAlpha: 0, y: 40 });
  gsap.set(beats[0], { autoAlpha: 1, y: 0 });
  gsap.set(ingot, { autoAlpha: 0, scale: 0.55 });
  gsap.set(sats, { autoAlpha: 0, y: 26 });
  gsap.set(specs, { autoAlpha: 0, y: 20 });
  gsap.set(steam, { autoAlpha: 0, x: (i) => (i % 2 ? 1 : -1) * (10 + i * 7) });
  tiles.forEach((t, i) =>
    gsap.to(t, { y: (i % 2 ? -1 : 1) * (6 + (i % 3) * 4), duration: 1.8 + (i % 4) * 0.35, yoyo: true, repeat: -1, ease: "sine.inOut" }),
  );

  gsap
    .timeline({ scrollTrigger: { trigger: ".qb-story", start: "top top", end: "bottom bottom", scrub: 1.2 } })
    .to({}, { duration: 0.5 })
    .addLabel("fuse")
    .to(beats[0], { autoAlpha: 0, y: -40, duration: 0.35 }, "fuse")
    .to(beats[1], { autoAlpha: 1, y: 0, duration: 0.4 }, "fuse+=0.25")
    .to(tiles, { left: "50%", top: "42%", scale: 0.45, duration: 1, ease: "power2.inOut", stagger: 0.03 }, "fuse")
    .to(forge, { "--h": 0, duration: 1.1, ease: "power1.inOut" }, "fuse+=0.25")
    .fromTo(steam, { autoAlpha: 0, y: 0, scale: 0.5 }, { autoAlpha: 1, y: -180, scale: 1.9, duration: 0.8, stagger: 0.04, ease: "power1.out" }, "fuse+=0.8")
    .to(steam, { autoAlpha: 0, duration: 0.5, stagger: 0.04 }, "fuse+=1.25")
    .to(tiles, { autoAlpha: 0, duration: 0.25 }, "fuse+=1.0")
    .fromTo(burst, { autoAlpha: 0.9, scale: 0.6 }, { autoAlpha: 0, scale: 13, duration: 0.9, ease: "power2.out" }, "fuse+=1.02")
    .to(ingot, { autoAlpha: 1, scale: 1, duration: 0.6, ease: "back.out(1.7)" }, "fuse+=1.05")
    .to({}, { duration: 0.45 })
    .addLabel("after")
    .to(beats[1], { autoAlpha: 0, y: -40, duration: 0.35 }, "after")
    .to(beats[2], { autoAlpha: 1, y: 0, duration: 0.4 }, "after+=0.25")
    .to(sats, { autoAlpha: 1, y: 0, duration: 0.5, stagger: 0.1 }, "after+=0.1")
    .to(specs, { autoAlpha: 1, y: 0, duration: 0.5, stagger: 0.1 }, "after+=0.35")
    .to({}, { duration: 0.7 });
}

function loadCard(el) {
  el.classList.add("is-in");
  gsap.to(el.querySelectorAll(".qb-pop"), { autoAlpha: 1, y: 0, duration: 0.9, ease: "expo.out", stagger: 0.05, delay: 0.15 });
  el.querySelectorAll(".qb-type").forEach((t, i) => {
    const n = Math.max(1, (t.textContent || "").length);
    gsap.fromTo(t, { clipPath: "inset(0 100% 0 0)" }, {
      clipPath: "inset(0 0% 0 0)", duration: Math.min(1.6, n * 0.026), ease: `steps(${n})`, delay: 1 + i * 1.1,
    });
  });
}

// ---------------------------------------------------------------------------
// Pointer effects

function setupSpotlight() {
  qa(".qb-card").forEach((c) =>
    c.addEventListener("pointermove", (e) => {
      const r = c.getBoundingClientRect();
      c.style.setProperty("--mx", `${e.clientX - r.left}px`);
      c.style.setProperty("--my", `${e.clientY - r.top}px`);
    }),
  );
}

function setupMagnets() {
  if (!window.matchMedia("(hover: hover)").matches) return;
  qa(".qb-mag").forEach((b) => {
    const xTo = gsap.quickTo(b, "x", { duration: 0.6, ease: "power3" });
    const yTo = gsap.quickTo(b, "y", { duration: 0.6, ease: "power3" });
    b.addEventListener("pointermove", (e) => {
      const r = b.getBoundingClientRect();
      xTo((e.clientX - r.left - r.width / 2) * 0.22);
      yTo((e.clientY - r.top - r.height / 2) * 0.32);
    });
    b.addEventListener("pointerleave", () => { xTo(0); yTo(0); });
  });
}

function setupTicketTilt() {
  const stage = q(".qb-invite__stage");
  const ticket = q(".qb-ticket");
  if (!stage || !ticket) return;
  const rx = gsap.quickTo(ticket, "rotationX", { duration: 0.7, ease: "power3" });
  const ry = gsap.quickTo(ticket, "rotationY", { duration: 0.7, ease: "power3" });
  stage.addEventListener("pointermove", (e) => {
    const r = stage.getBoundingClientRect();
    const px = (e.clientX - r.left) / r.width - 0.5;
    const py = (e.clientY - r.top) / r.height - 0.5;
    ry(px * 18);
    rx(-py * 14);
    ticket.style.setProperty("--sx", `${(100 - (px + 0.5) * 100).toFixed(1)}%`);
  });
  stage.addEventListener("pointerleave", () => { rx(0); ry(0); });
}

// ---------------------------------------------------------------------------
// Code tabs (JavaScript / Python / cURL)

function setupTabs() {
  const tabs = qa("[data-tab]");
  const select = (tab, focus) => {
    tabs.forEach((t) => {
      const on = t === tab;
      t.classList.toggle("on", on);
      t.setAttribute("aria-selected", String(on));
      t.tabIndex = on ? 0 : -1;
      document.getElementById(t.getAttribute("aria-controls")).hidden = !on;
    });
    if (focus) tab.focus();
  };
  tabs.forEach((t, i) => {
    t.addEventListener("click", () => select(t));
    t.addEventListener("keydown", (e) => {
      const step = e.key === "ArrowRight" ? 1 : e.key === "ArrowLeft" ? -1 : 0;
      if (!step) return;
      e.preventDefault();
      select(tabs[(i + step + tabs.length) % tabs.length], true);
    });
  });
}

// ---------------------------------------------------------------------------
// The product tour: a scripted timeline with chapters, play/pause and a clock.

function setupFilm() {
  const stage = q(".fstage");
  const screen = q(".qb-screen");
  const poster = q("[data-film-poster]");
  const playBtn = q("[data-film-play]");
  const clock = q("[data-film-time]");
  const chapters = qa("[data-chapter]");
  const bars = chapters.map((c) => c.querySelector(".qb-bar i"));
  const starts = [0, 7.4, 19.4, 28.6, 34.1, 41.5];
  const fmt = (t) => `${Math.floor(t / 60)}:${String(Math.floor(t % 60)).padStart(2, "0")}`;

  let tl = null;
  let loaded = false;
  let userPaused = false;
  let lastChapter = -1;
  let lastSecond = -1;

  const setPlaying = (on) => {
    playBtn.setAttribute("aria-label", on ? "Pause the tour" : "Play the tour");
    // SVG elements have no .hidden property, so toggle the attribute itself.
    playBtn.querySelector('[data-icon="pause"]').toggleAttribute("hidden", !on);
    playBtn.querySelector('[data-icon="play"]').toggleAttribute("hidden", on);
  };

  const tick = () => {
    const t = tl.time();
    for (let i = 0; i < 5; i++) {
      const p = Math.max(0, Math.min(1, (t - starts[i]) / (starts[i + 1] - starts[i])));
      bars[i].style.transform = `scaleX(${p.toFixed(4)})`;
    }
    let ch = 0;
    for (let i = 0; i < 5; i++) if (t >= starts[i]) ch = i;
    if (ch !== lastChapter) {
      lastChapter = ch;
      chapters.forEach((c, i) => c.classList.toggle("on", i === ch));
    }
    const sec = Math.floor(t);
    if (sec !== lastSecond) {
      lastSecond = sec;
      clock.textContent = `${fmt(sec)} / ${fmt(41)}`;
    }
  };

  const ensure = () => {
    if (!tl) tl = buildFilm(stage, tick, () => { userPaused = true; setPlaying(false); });
    return tl;
  };

  const play = (label) => {
    const t = ensure();
    screen.classList.add("is-in");
    poster.hidden = true;
    if (label !== undefined) t.play(label);
    else if (t.progress() >= 1) t.restart();
    else t.play();
    setPlaying(true);
  };

  const toggle = () => {
    const t = ensure();
    if (t.isActive()) {
      t.pause();
      userPaused = true;
      setPlaying(false);
    } else {
      userPaused = false;
      play();
    }
  };

  qa("[data-film-toggle]").forEach((b) => b.addEventListener("click", toggle));
  chapters.forEach((c, i) => c.addEventListener("click", () => { userPaused = false; play(`c${i}`); }));

  return {
    // Shows a short loading shimmer the first time, then plays.
    enter() {
      if (!loaded) {
        loaded = true;
        setTimeout(() => { screen.classList.add("is-in"); if (!userPaused) play(); }, 750);
        return;
      }
      if (!userPaused && tl && tl.progress() < 1 && !tl.isActive()) play();
    },
    pauseForScroll() {
      if (tl && tl.isActive()) { tl.pause(); setPlaying(false); }
    },
  };
}

function buildFilm(st, onTick, onDone) {
  const q1 = (s) => st.querySelector(s);
  const qn = (s) => Array.from(st.querySelectorAll(s));
  const sr = st.getBoundingClientRect();
  const k = sr.width / 1200 || 1;
  // Where to point the cursor, in the stage's own 1200×675 coordinates.
  const P = (s) => {
    const el = q1(s);
    if (!el) return { x: 600, y: 600 };
    const r = el.getBoundingClientRect();
    return { x: (r.left + r.width / 2 - sr.left) / k, y: (r.top + r.height / 2 - sr.top) / k };
  };
  const pt = {
    code: P(".fi-code"), b1: P(".fb-1"), name: P(".fi-name"), mail: P(".fi-mail"), pass: P(".fi-pass"), b2: P(".fb-2"), proj: P(".fi-proj"), b3: P(".fb-3"),
    nw: P(".fd-new"), tname: P(".fi-tname"), who: P(".fd-who"), sql: P(".fd-showsql"), create: P(".fd-create"),
    connect: P(".fd-connect"), copy: P(".fd-copy--k"), insert: P(".fe-insert"), input: P(".fe-input"),
  };

  const tl = gsap.timeline({ paused: true, defaults: { ease: "power3.out" }, onUpdate: () => onTick(), onComplete: onDone });
  const cur = q1(".fcur");
  const ring = q1(".fclick");
  const scenes = { a: q1(".fs-a"), b: q1(".fs-b"), c: q1(".fs-c"), e: q1(".fs-e"), end: q1(".fs-end") };

  tl.set([scenes.b, scenes.c, scenes.e, scenes.end], { autoAlpha: 0 }, 0)
    .set(scenes.a, { autoAlpha: 1 }, 0)
    .set(qn(".fty"), { clipPath: "inset(0 100% 0 0)" }, 0)
    .set(qn(".ta-o, .ta-box, .ta-l2line"), { autoAlpha: 0 }, 0)
    .set(qn(".fs .fcap, .fs .fwin"), { autoAlpha: 0 }, 0)
    .set(qn(".fw-c2, .fw-c3, .fw-c4"), { autoAlpha: 0, x: 40 }, 0)
    .set(q1(".fw-c1"), { autoAlpha: 1, x: 0 }, 0)
    .set(qn(".fstr i"), { scaleX: 0 }, 0)
    .set(q1(".fd-empty"), { autoAlpha: 1 }, 0)
    .set(q1(".fd-table"), { autoAlpha: 0 }, 0)
    .set(qn(".fd-r"), { autoAlpha: 0, y: 8 }, 0)
    .set(q1(".fd-sheet"), { autoAlpha: 0, xPercent: 100 }, 0)
    .set(qn(".fd-col"), { autoAlpha: 0, y: 8 }, 0)
    .set(q1(".fd-sql"), { height: 0 }, 0)
    .set(q1(".fd-glow"), { opacity: 0 }, 0)
    .set([q1(".fd-dim"), q1(".fd-dialog")], { autoAlpha: 0 }, 0)
    .set(qn(".fd-code .ln"), { autoAlpha: 0, x: -8 }, 0)
    .set(q1(".fd-copy-a"), { autoAlpha: 1 }, 0)
    .set(q1(".fd-copy-b"), { autoAlpha: 0 }, 0)
    .set(q1(".fe-new"), { height: 0, autoAlpha: 0 }, 0)
    .set(q1(".fe-rownew"), { autoAlpha: 0 }, 0)
    .set(cur, { x: 600, y: 640, autoAlpha: 0, scale: 1 }, 0)
    .set(ring, { autoAlpha: 0 }, 0);

  const mv = (p, at, d = 0.8) => tl.to(cur, { x: p.x, y: p.y, duration: d, ease: "power3.inOut" }, at);
  const click = (p, at) => {
    tl.to(cur, { scale: 0.82, duration: 0.09, yoyo: true, repeat: 1, ease: "power1.inOut" }, at);
    tl.set(ring, { x: p.x, y: p.y }, at);
    tl.fromTo(ring, { autoAlpha: 0.95, scale: 0.25 }, { autoAlpha: 0, scale: 1.5, duration: 0.5, ease: "power2.out", immediateRender: false }, at);
  };
  const type = (s, at, d) => {
    const el = q1(s);
    if (!el) return;
    const n = Math.max(1, (el.textContent || "").length);
    tl.to(el, { clipPath: "inset(0 0% 0 0)", duration: d || n * 0.055, ease: `steps(${n})` }, at);
  };
  const winIn = (sc, at) =>
    tl.fromTo(sc.querySelectorAll(".fwin"), { autoAlpha: 0, y: 34, scale: 0.975 }, { autoAlpha: 1, y: 0, scale: 1, duration: 0.85, ease: "expo.out", stagger: 0.1, immediateRender: false }, at);
  const capIn = (el, at) => tl.fromTo(el, { autoAlpha: 0, y: 16 }, { autoAlpha: 1, y: 0, duration: 0.6, ease: "expo.out", immediateRender: false }, at);
  const capOut = (el, at) => tl.to(el, { autoAlpha: 0, y: 10, duration: 0.35, ease: "power2.in" }, at);
  const sceneOut = (sc, at) => tl.to(sc, { autoAlpha: 0, scale: 0.97, duration: 0.5, ease: "power2.in" }, at).set(sc, { scale: 1 }, at + 0.55);

  // 01 Start it
  tl.addLabel("c0", 0);
  winIn(scenes.a, 0.15);
  capIn(scenes.a.querySelector(".fcap"), 0.3);
  type(".ta-l1", 0.95, 1.1);
  tl.to(qn(".ta-o"), { autoAlpha: 1, duration: 0.25, stagger: 0.22, ease: "none" }, 2.3);
  tl.to(q1(".ta-l2line"), { autoAlpha: 1, duration: 0.1 }, 3.5);
  type(".ta-l2", 3.6, 1.3);
  tl.fromTo(q1(".ta-box"), { autoAlpha: 0, y: 10 }, { autoAlpha: 1, y: 0, duration: 0.6, ease: "expo.out", immediateRender: false }, 5.15);
  tl.fromTo(q1(".ta-box b"), { boxShadow: "0 0 0 0 rgba(76,201,217,.8)" }, { boxShadow: "0 0 0 10px rgba(76,201,217,0)", duration: 0.9, repeat: 1, immediateRender: false }, 5.7);
  sceneOut(scenes.a, 6.9);

  // 02 Set up
  tl.addLabel("c1", 7.4);
  tl.set(scenes.b, { autoAlpha: 1 }, 7.4);
  winIn(scenes.b, 7.4);
  capIn(scenes.b.querySelector(".fcap"), 7.6);
  tl.to(cur, { autoAlpha: 1, duration: 0.3 }, 7.95);
  mv(pt.code, 8.05, 0.85); click(pt.code, 8.9); type(".fty-code", 8.95, 0.7);
  mv(pt.b1, 9.8, 0.6); click(pt.b1, 10.4);
  tl.to(q1(".fw-c1"), { autoAlpha: 0, x: -40, duration: 0.45, ease: "power2.in" }, 10.55)
    .to(q1(".fw-c2"), { autoAlpha: 1, x: 0, duration: 0.6, ease: "expo.out" }, 10.75);
  mv(pt.name, 11.2, 0.5); click(pt.name, 11.7); type(".fty-name", 11.75, 0.6);
  mv(pt.mail, 12.4, 0.4); click(pt.mail, 12.8); type(".fty-mail", 12.85, 0.6);
  mv(pt.pass, 13.5, 0.4); click(pt.pass, 13.9); type(".fty-pass", 13.95, 0.5);
  tl.to(qn(".fstr i"), { scaleX: 1, duration: 0.3, stagger: 0.1, ease: "power2.out" }, 14.15);
  mv(pt.b2, 14.6, 0.5); click(pt.b2, 15.1);
  tl.to(q1(".fw-c2"), { autoAlpha: 0, x: -40, duration: 0.45, ease: "power2.in" }, 15.25)
    .to(q1(".fw-c3"), { autoAlpha: 1, x: 0, duration: 0.6, ease: "expo.out" }, 15.45);
  mv(pt.proj, 15.8, 0.5); click(pt.proj, 16.3); type(".fty-proj", 16.35, 0.5);
  mv(pt.b3, 17.0, 0.5); click(pt.b3, 17.5);
  tl.to(q1(".fw-c3"), { autoAlpha: 0, x: -40, duration: 0.45, ease: "power2.in" }, 17.65)
    .to(q1(".fw-c4"), { autoAlpha: 1, x: 0, duration: 0.6, ease: "expo.out" }, 17.85);
  tl.to(cur, { autoAlpha: 0, duration: 0.3 }, 18.6);
  sceneOut(scenes.b, 18.9);

  // 03 Make a table
  tl.addLabel("c2", 19.4);
  tl.set(scenes.c, { autoAlpha: 1 }, 19.4);
  winIn(scenes.c, 19.4);
  capIn(q1(".fcap-c"), 19.6);
  tl.set(cur, { x: 640, y: 560 }, 19.9).to(cur, { autoAlpha: 1, duration: 0.3 }, 19.95);
  mv(pt.nw, 20.05, 0.8); click(pt.nw, 20.9);
  tl.to(q1(".fd-sheet"), { autoAlpha: 1, xPercent: 0, duration: 0.65, ease: "expo.out" }, 21.0);
  mv(pt.tname, 21.7, 0.5); click(pt.tname, 22.2); type(".fty-tname", 22.25, 0.4);
  tl.to(qn(".fd-col"), { autoAlpha: 1, y: 0, duration: 0.4, stagger: 0.18, ease: "expo.out" }, 22.8);
  mv(pt.who, 23.5, 0.5);
  tl.to(q1(".fd-glow"), { opacity: 1, duration: 0.3, yoyo: true, repeat: 3, ease: "sine.inOut" }, 24.0);
  mv(pt.sql, 24.6, 0.45); click(pt.sql, 25.05);
  tl.to(q1(".fd-sql"), { height: "auto", duration: 0.6, ease: "expo.out" }, 25.1);
  mv(pt.create, 26.4, 0.6); click(pt.create, 27.0);
  tl.to(q1(".fd-sheet"), { autoAlpha: 0, xPercent: 100, duration: 0.5, ease: "power3.in" }, 27.1)
    .to(q1(".fd-empty"), { autoAlpha: 0, duration: 0.3 }, 27.2)
    .to(q1(".fd-table"), { autoAlpha: 1, duration: 0.4 }, 27.4)
    .to(qn(".fd-r"), { autoAlpha: 1, y: 0, duration: 0.45, stagger: 0.12, ease: "expo.out" }, 27.6);

  // 04 Connect
  tl.addLabel("c3", 28.6);
  capOut(q1(".fcap-c"), 28.6);
  capIn(q1(".fcap-d"), 28.8);
  mv(pt.connect, 28.8, 0.7); click(pt.connect, 29.5);
  tl.to(q1(".fd-dim"), { autoAlpha: 1, duration: 0.3 }, 29.6)
    .fromTo(q1(".fd-dialog"), { autoAlpha: 0, scale: 0.96, y: 12 }, { autoAlpha: 1, scale: 1, y: 0, duration: 0.55, ease: "expo.out", immediateRender: false }, 29.65)
    .to(qn(".fd-code .ln"), { autoAlpha: 1, x: 0, duration: 0.35, stagger: 0.09, ease: "power2.out" }, 30.3);
  mv(pt.copy, 31.3, 0.6); click(pt.copy, 31.9);
  tl.to(q1(".fd-copy-a"), { autoAlpha: 0, duration: 0.15 }, 32.0)
    .to(q1(".fd-copy-b"), { autoAlpha: 1, duration: 0.2 }, 32.1)
    .to(q1(".fd-dialog"), { autoAlpha: 0, scale: 0.97, duration: 0.35, ease: "power2.in" }, 33.2)
    .to(q1(".fd-dim"), { autoAlpha: 0, duration: 0.3 }, 33.3)
    .to(cur, { autoAlpha: 0, duration: 0.25 }, 33.4);
  sceneOut(scenes.c, 33.6);

  // 05 Go live
  tl.addLabel("c4", 34.1);
  tl.set(scenes.e, { autoAlpha: 1 }, 34.1);
  winIn(scenes.e, 34.1);
  capIn(scenes.e.querySelector(".fcap"), 34.3);
  tl.set(cur, { x: 860, y: 600 }, 34.6).to(cur, { autoAlpha: 1, duration: 0.3 }, 34.7);
  mv(pt.insert, 34.8, 0.7); click(pt.insert, 35.5);
  tl.fromTo(q1(".fe-rownew"), { autoAlpha: 0, y: -6 }, { autoAlpha: 1, y: 0, duration: 0.35, immediateRender: false }, 35.6);
  mv(pt.input, 35.7, 0.4);
  type(".fty-new", 35.95, 1.0);
  tl.to(q1(".fe-rownew"), { backgroundColor: "rgba(76,201,217,0.28)", duration: 0.2, yoyo: true, repeat: 1 }, 37.1)
    .to(q1(".fe-new"), { height: "auto", autoAlpha: 1, duration: 0.6, ease: "expo.out" }, 37.3)
    .fromTo(q1(".fe-live"), { scale: 1 }, { scale: 1.14, duration: 0.2, yoyo: true, repeat: 3, ease: "sine.inOut", immediateRender: false }, 37.3)
    .to(cur, { autoAlpha: 0, duration: 0.3 }, 38.6);
  sceneOut(scenes.e, 39.0);

  // End card
  tl.set(scenes.end, { autoAlpha: 1 }, 39.5)
    .fromTo(scenes.end.querySelector("svg"), { scale: 0.6, autoAlpha: 0 }, { scale: 1, autoAlpha: 1, duration: 0.8, ease: "back.out(1.8)", immediateRender: false }, 39.5)
    .fromTo(scenes.end.querySelectorAll("h4, p"), { autoAlpha: 0, y: 16 }, { autoAlpha: 1, y: 0, duration: 0.8, stagger: 0.1, ease: "expo.out", immediateRender: false }, 39.7)
    .to({}, { duration: 0.1 }, 41.4);
  return tl;
}
